/**
 * Toast Notification Context
 *
 * Provides a centralized toast notification system with:
 * - Multiple toast types (success, error, warning, info)
 * - Queue management for multiple toasts
 * - Auto-dismiss functionality
 * - Action buttons support
 * - Positioning options
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { Alert, Snackbar, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const ToastContext = createContext(null);

// Toast types matching error severity
export const ToastType = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

// Default toast configuration
const DEFAULT_CONFIG = {
  duration: 6000,
  position: { vertical: 'bottom', horizontal: 'center' },
  maxToasts: 3,
};

export const ToastProvider = ({ children, config = {} }) => {
  const [toasts, setToasts] = useState([]);
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };

  /**
   * Dismiss a specific toast
   */
  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  /**
   * Show a toast notification
   */
  const showToast = useCallback((message, options = {}) => {
    const {
      type = ToastType.INFO,
      duration = mergedConfig.duration,
      action = null,
      persistent = false,
      id = Date.now() + Math.random(),
    } = options;

    const newToast = {
      id,
      message,
      type,
      duration,
      action,
      persistent,
    };

    setToasts((prev) => {
      // Limit number of toasts
      const updatedToasts = [...prev, newToast];
      if (updatedToasts.length > mergedConfig.maxToasts) {
        return updatedToasts.slice(-mergedConfig.maxToasts);
      }
      return updatedToasts;
    });

    // Auto-dismiss if not persistent
    if (!persistent && duration > 0) {
      setTimeout(() => {
        dismissToast(id);
      }, duration);
    }

    return id;
  }, [mergedConfig.duration, mergedConfig.maxToasts, dismissToast]);

  /**
   * Show success toast
   */
  const showSuccess = useCallback((message, options = {}) => {
    return showToast(message, { ...options, type: ToastType.SUCCESS });
  }, [showToast]);

  /**
   * Show error toast
   */
  const showError = useCallback((message, options = {}) => {
    return showToast(message, { ...options, type: ToastType.ERROR });
  }, [showToast]);

  /**
   * Show warning toast
   */
  const showWarning = useCallback((message, options = {}) => {
    return showToast(message, { ...options, type: ToastType.WARNING });
  }, [showToast]);

  /**
   * Show info toast
   */
  const showInfo = useCallback((message, options = {}) => {
    return showToast(message, { ...options, type: ToastType.INFO });
  }, [showToast]);

  /**
   * Dismiss all toasts
   */
  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  const value = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    dismissToast,
    dismissAll,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.map((toast, index) => (
        <Snackbar
          key={toast.id}
          open={true}
          autoHideDuration={toast.persistent ? null : toast.duration}
          onClose={() => dismissToast(toast.id)}
          anchorOrigin={{
            ...mergedConfig.position,
            // Stack multiple toasts vertically
            vertical: mergedConfig.position.vertical === 'bottom'
              ? 'bottom'
              : 'top',
          }}
          style={{
            // Stack multiple toasts with offset
            [mergedConfig.position.vertical]: `${index * 80 + 16}px`,
          }}
        >
          <Alert
            severity={toast.type}
            onClose={() => dismissToast(toast.id)}
            sx={{ width: '100%', minWidth: '300px' }}
            action={
              <>
                {toast.action && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      toast.action.onClick();
                      dismissToast(toast.id);
                    }}
                  >
                    {toast.action.label}
                  </Button>
                )}
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={() => dismissToast(toast.id)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
          >
            {toast.message}
          </Alert>
        </Snackbar>
      ))}
    </ToastContext.Provider>
  );
};

/**
 * Hook to use toast notifications
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;
